// components/appointments/BlockTimeModal.jsx
import React, { useState, useEffect } from "react";
import {
  X,
  Calendar,
  Clock,
  Users,
  AlertCircle,
  Loader,
  Ban,
  Coffee,
  Book,
  Briefcase,
  Wrench,
  Phone,
  CheckCircle,
} from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const BlockTimeModal = ({ onClose, onSuccess, teamId, preselectedMember }) => {
  const { isDarkMode } = useTheme();
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(teamId || null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    selectedMembers: preselectedMember ? [preselectedMember] : [],
    startTime: "",
    endTime: "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    blockType: "break",
    title: "",
    description: "",
    isRecurring: false,
    recurrencePattern: "daily",
    recurrenceInterval: 1,
    recurrenceEndDate: "",
    applyToTeam: false,
  });

  const [previewCount, setPreviewCount] = useState(0);

  const blockTypes = [
    {
      value: "break",
      label: "Break",
      icon: Coffee,
      color: "#F59E0B",
      description: "Lunch, coffee break, etc.",
    },
    {
      value: "training",
      label: "Training",
      icon: Book,
      color: "#8B5CF6",
      description: "Training session, workshop",
    },
    {
      value: "meeting",
      label: "Internal Meeting",
      icon: Briefcase,
      color: "#3B82F6",
      description: "Team meetings, reviews",
    },
    {
      value: "maintenance",
      label: "Maintenance",
      icon: Wrench,
      color: "#EF4444",
      description: "System maintenance, updates",
    },
    {
      value: "unavailable",
      label: "Unavailable",
      icon: Ban,
      color: "#6B7280",
      description: "Personal time, out of office",
    },
    {
      value: "call_block",
      label: "Call Block",
      icon: Phone,
      color: "#10B981",
      description: "Reserved time for calls",
    },
  ];

  useEffect(() => {
    fetchUserTeams();
  }, []);

  useEffect(() => {
    if (selectedTeam) {
      fetchTeamMembers();
    }
  }, [selectedTeam]);

  useEffect(() => {
    if (formData.startTime && formData.endTime && formData.isRecurring) {
      calculatePreviewCount();
    } else {
      setPreviewCount(0);
    }
  }, [
    formData.startTime,
    formData.endTime,
    formData.isRecurring,
    formData.recurrencePattern,
    formData.recurrenceInterval,
    formData.recurrenceEndDate,
  ]);

  const fetchUserTeams = async () => {
    try {
      const response = await apiService.getUserTeams();
      if (response.success) {
        const managerTeams = response.teams.filter((t) =>
          ["manager", "team_lead"].includes(t.userRole),
        );
        setTeams(managerTeams);

        if (teamId) {
          setSelectedTeam(teamId);
        } else if (managerTeams.length > 0) {
          setSelectedTeam(managerTeams[0].id);
        }
      }
    } catch (error) {
      console.error("Error fetching teams:", error);
      setError("Failed to load teams");
    }
  };

  const fetchTeamMembers = async () => {
    try {
      setLoadingMembers(true);
      const response = await apiService.getTeamMembers3(selectedTeam, {
        status: "active",
      });

      if (response.success) {
        setTeamMembers(response.members);
        const actualTeamIds = [
          ...new Set(response.members.map((m) => m.teamId)),
        ];
        setFormData((prev) => ({
          ...prev,
          actualTeamIds,
        }));
      }
    } catch (error) {
      console.error("Error fetching team members:", error);
      setError("Failed to load team members");
    } finally {
      setLoadingMembers(false);
    }
  };

  const calculatePreviewCount = () => {
    if (
      !formData.startTime ||
      !formData.endTime ||
      !formData.recurrenceEndDate
    ) {
      setPreviewCount(0);
      return;
    }

    const start = new Date(formData.startTime);
    const end = new Date(formData.recurrenceEndDate);
    const interval = formData.recurrenceInterval;

    let count = 0;
    let currentDate = new Date(start);

    while (currentDate <= end) {
      count++;
      switch (formData.recurrencePattern) {
        case "daily":
          currentDate.setDate(currentDate.getDate() + interval);
          break;
        case "weekly":
          currentDate.setDate(currentDate.getDate() + 7 * interval);
          break;
        case "monthly":
          currentDate.setMonth(currentDate.getMonth() + interval);
          break;
      }
      if (count > 365) break;
    }

    setPreviewCount(count);
  };

  const toggleMemberSelection = (userId) => {
    setFormData((prev) => {
      const isSelected = prev.selectedMembers.includes(userId);
      return {
        ...prev,
        selectedMembers: isSelected
          ? prev.selectedMembers.filter((id) => id !== userId)
          : [...prev.selectedMembers, userId],
      };
    });
  };

  const selectAllMembers = () => {
    setFormData((prev) => ({
      ...prev,
      selectedMembers: teamMembers.map((m) => m.userId),
    }));
  };

  const deselectAllMembers = () => {
    setFormData((prev) => ({
      ...prev,
      selectedMembers: [],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (formData.selectedMembers.length === 0) {
      setError("Please select at least one team member");
      return;
    }

    const start = new Date(formData.startTime);
    const end = new Date(formData.endTime);

    if (end <= start) {
      setError("End time must be after start time");
      return;
    }

    if (
      formData.isRecurring &&
      (!formData.recurrenceEndDate || previewCount === 0)
    ) {
      setError("Please specify a valid recurrence end date");
      return;
    }

    setLoading(true);

    try {
      const selectedBlockType = blockTypes.find(
        (t) => t.value === formData.blockType,
      );

      const membersByTeam = {};
      formData.selectedMembers.forEach((memberId) => {
        const member = teamMembers.find((m) => m.userId === memberId);
        if (member) {
          const actualTeamId = member.teamId;
          if (!membersByTeam[actualTeamId]) {
            membersByTeam[actualTeamId] = [];
          }
          membersByTeam[actualTeamId].push(memberId);
        }
      });

      const results = [];
      const errors = [];

      for (const [actualTeamId, memberIds] of Object.entries(membersByTeam)) {
        try {
          const response = await apiService.blockTeamMemberTime({
            teamId: actualTeamId,
            memberIds: memberIds,
            startTime: formData.startTime,
            endTime: formData.endTime,
            timezone: formData.timezone,
            blockType: formData.blockType,
            title:
              formData.title || `${selectedBlockType.label} - Time Blocked`,
            description: formData.description,
            isRecurring: formData.isRecurring,
            recurrencePattern: formData.isRecurring
              ? formData.recurrencePattern
              : null,
            recurrenceInterval: formData.isRecurring
              ? formData.recurrenceInterval
              : null,
            recurrenceEndDate: formData.isRecurring
              ? formData.recurrenceEndDate
              : null,
          });

          if (response.success) {
            results.push(response);
          }
        } catch (error) {
          console.error(`Error blocking time for team ${actualTeamId}:`, error);
          errors.push({
            teamId: actualTeamId,
            error: error.response?.data?.message || error.message,
          });
        }
      }

      if (results.length > 0) {
        const totalCreated = results.reduce(
          (sum, r) => sum + r.results.totalCreated,
          0,
        );
        onSuccess({ success: true, totalCreated, results, errors });
      } else {
        setError("Failed to block time for all selected members");
      }
    } catch (error) {
      console.error("Error blocking time:", error);
      setError(error.response?.data?.message || "Failed to block time");
    } finally {
      setLoading(false);
    }
  };

  const selectedBlockType = blockTypes.find(
    (t) => t.value === formData.blockType,
  );

  // Shared input class helper
  const inputCls = `w-full px-4 py-3 border rounded-xl focus:outline-none focus:border-orange-500/50 focus:ring-2 focus:ring-orange-500/20 transition-all duration-200 ${
    isDarkMode
      ? "bg-gray-900/50 border-gray-700/50 text-white placeholder-gray-500"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
  }`;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div
        className={`rounded-2xl shadow-2xl border w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col ${
          isDarkMode
            ? "bg-gradient-to-br from-[#0C0E12] to-[#0A0C10] border-gray-800/50"
            : "bg-white border-gray-200"
        }`}
      >
        {/* Header */}
        <div
          className={`relative border-b p-6 ${
            isDarkMode
              ? "bg-gradient-to-r from-[#0C0E12] via-[#0E1015] to-[#0C0E12] border-gray-800/50"
              : "bg-gray-50 border-gray-200"
          }`}
        >
          {isDarkMode && (
            <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 to-transparent pointer-events-none"></div>
          )}
          <div className="relative flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <div
                  className={`w-10 h-10 rounded-xl border flex items-center justify-center ${
                    isDarkMode
                      ? "bg-gradient-to-br from-orange-500/20 to-orange-600/10 border-orange-500/20"
                      : "bg-orange-50 border-orange-200"
                  }`}
                >
                  <Ban
                    size={20}
                    className={
                      isDarkMode ? "text-orange-400" : "text-orange-500"
                    }
                  />
                </div>
                <h2
                  className={`text-2xl font-semibold tracking-tight ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Block Team Member Time
                </h2>
              </div>
              <p
                className={`text-sm ml-13 font-light ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Reserve time slots for breaks, training, or other activities
              </p>
            </div>
            <button
              onClick={onClose}
              className={`group w-9 h-9 rounded-lg border flex items-center justify-center transition-all duration-200 ${
                isDarkMode
                  ? "bg-gray-800/50 hover:bg-gray-700/50 border-gray-700/50 hover:border-gray-600/50 text-gray-400 hover:text-white"
                  : "bg-white hover:bg-gray-100 border-gray-200 hover:border-gray-300 text-gray-500 hover:text-gray-900"
              }`}
            >
              <X
                size={18}
                className="group-hover:rotate-90 transition-transform duration-200"
              />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Error Alert */}
            {error && (
              <div
                className={`border rounded-xl p-4 animate-slideDown ${
                  isDarkMode
                    ? "bg-gradient-to-br from-red-500/10 to-red-600/5 border-red-500/30"
                    : "bg-red-50 border-red-200"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isDarkMode ? "bg-red-500/20" : "bg-red-100"}`}
                  >
                    <AlertCircle
                      size={16}
                      className={isDarkMode ? "text-red-400" : "text-red-500"}
                    />
                  </div>
                  <div className="flex-1">
                    <p
                      className={`text-sm font-medium ${isDarkMode ? "text-red-300" : "text-red-700"}`}
                    >
                      {error}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Team Selection */}
            {!teamId && teams.length > 1 && (
              <div>
                <label
                  className={`block text-sm font-medium mb-2.5 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                >
                  Select Team
                </label>
                <select
                  value={selectedTeam || ""}
                  onChange={(e) => setSelectedTeam(e.target.value)}
                  className={inputCls}
                >
                  {teams.map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Block Type Selection */}
            <div>
              <label
                className={`block text-sm font-medium mb-3 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                Block Type
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {blockTypes.map((type) => {
                  const Icon = type.icon;
                  const isSelected = formData.blockType === type.value;

                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          blockType: type.value,
                        }))
                      }
                      className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                        isSelected
                          ? "border-opacity-50 shadow-lg"
                          : isDarkMode
                            ? "border-gray-700/50 hover:border-gray-600/50"
                            : "border-gray-200 hover:border-gray-300"
                      }`}
                      style={{
                        borderColor: isSelected ? type.color : undefined,
                        backgroundColor: isSelected
                          ? `${type.color}15`
                          : isDarkMode
                            ? "rgba(17, 24, 39, 0.5)"
                            : "rgba(249, 250, 251, 1)",
                      }}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div
                          className="w-8 h-8 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${type.color}20` }}
                        >
                          <Icon size={16} style={{ color: type.color }} />
                        </div>
                        <span
                          className="font-semibold text-sm"
                          style={{
                            color: isSelected
                              ? type.color
                              : isDarkMode
                                ? "#fff"
                                : "#111827",
                          }}
                        >
                          {type.label}
                        </span>
                      </div>
                      <p
                        className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        {type.description}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Title & Description */}
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label
                  className={`block text-sm font-medium mb-2.5 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                >
                  Title (Optional)
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, title: e.target.value }))
                  }
                  className={inputCls}
                  placeholder={`${selectedBlockType?.label} - Time Blocked`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2.5 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                >
                  Description (Optional)
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  rows={2}
                  className={`${inputCls} resize-none`}
                  placeholder="Add notes about this time block..."
                />
              </div>
            </div>

            {/* Time Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  className={`block text-sm font-medium mb-2.5 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                >
                  Start Time <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <Calendar
                      size={16}
                      className={isDarkMode ? "text-gray-400" : "text-gray-400"}
                    />
                  </div>
                  <input
                    type="datetime-local"
                    required
                    value={formData.startTime}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        startTime: e.target.value,
                      }))
                    }
                    className={`w-full pl-11 pr-4 py-3 border rounded-xl focus:outline-none focus:border-orange-500/50 focus:ring-2 focus:ring-orange-500/20 transition-all duration-200 ${
                      isDarkMode
                        ? "bg-gray-900/50 border-gray-700/50 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2.5 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                >
                  End Time <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <div className="absolute left-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <Clock
                      size={16}
                      className={isDarkMode ? "text-gray-400" : "text-gray-400"}
                    />
                  </div>
                  <input
                    type="datetime-local"
                    required
                    value={formData.endTime}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        endTime: e.target.value,
                      }))
                    }
                    className={`w-full pl-11 pr-4 py-3 border rounded-xl focus:outline-none focus:border-orange-500/50 focus:ring-2 focus:ring-orange-500/20 transition-all duration-200 ${
                      isDarkMode
                        ? "bg-gray-900/50 border-gray-700/50 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>
              </div>
            </div>

            {/* Recurring Options */}
            <div
              className={`border rounded-xl p-4 ${
                isDarkMode
                  ? "bg-gray-900/30 border-gray-700/50"
                  : "bg-gray-50 border-gray-200"
              }`}
            >
              <label className="flex items-center gap-3 cursor-pointer mb-4">
                <input
                  type="checkbox"
                  checked={formData.isRecurring}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      isRecurring: e.target.checked,
                    }))
                  }
                  className="w-5 h-5 rounded border-gray-600 text-orange-600 focus:ring-orange-500"
                />
                <div>
                  <span
                    className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    Make this recurring
                  </span>
                  <p
                    className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Block the same time slot on multiple days
                  </p>
                </div>
              </label>

              {formData.isRecurring && (
                <div
                  className={`space-y-4 pt-4 border-t ${isDarkMode ? "border-gray-700/50" : "border-gray-200"}`}
                >
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label
                        className={`block text-xs font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                      >
                        Repeat Pattern
                      </label>
                      <select
                        value={formData.recurrencePattern}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            recurrencePattern: e.target.value,
                          }))
                        }
                        className={`w-full px-3 py-2 border rounded-lg text-sm ${
                          isDarkMode
                            ? "bg-gray-800/50 border-gray-700/50 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        }`}
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>

                    <div>
                      <label
                        className={`block text-xs font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                      >
                        Every
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="365"
                        value={formData.recurrenceInterval}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            recurrenceInterval: parseInt(e.target.value),
                          }))
                        }
                        className={`w-full px-3 py-2 border rounded-lg text-sm ${
                          isDarkMode
                            ? "bg-gray-800/50 border-gray-700/50 text-white"
                            : "bg-white border-gray-300 text-gray-900"
                        }`}
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      className={`block text-xs font-medium mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                    >
                      End Date <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="date"
                      required={formData.isRecurring}
                      value={formData.recurrenceEndDate}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          recurrenceEndDate: e.target.value,
                        }))
                      }
                      className={`w-full px-3 py-2 border rounded-lg text-sm ${
                        isDarkMode
                          ? "bg-gray-800/50 border-gray-700/50 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    />
                  </div>

                  {previewCount > 0 && (
                    <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3">
                      <div className="flex items-center gap-2">
                        <CheckCircle size={16} className="text-orange-400" />
                        <span className="text-sm text-orange-300 font-medium">
                          This will create {previewCount} time block
                          {previewCount !== 1 ? "s" : ""}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Member Selection */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label
                  className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                >
                  Select Team Members <span className="text-red-400">*</span>
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={selectAllMembers}
                    className="text-xs text-orange-400 hover:text-orange-300 font-medium"
                  >
                    Select All
                  </button>
                  <span
                    className={isDarkMode ? "text-gray-600" : "text-gray-300"}
                  >
                    |
                  </span>
                  <button
                    type="button"
                    onClick={deselectAllMembers}
                    className={`text-xs font-medium ${isDarkMode ? "text-gray-400 hover:text-gray-300" : "text-gray-500 hover:text-gray-700"}`}
                  >
                    Clear
                  </button>
                </div>
              </div>

              {loadingMembers ? (
                <div className="flex items-center justify-center py-8">
                  <Loader className="animate-spin text-orange-400" size={24} />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-64 overflow-y-auto p-1">
                  {teamMembers.map((member) => {
                    const isSelected = formData.selectedMembers.includes(
                      member.userId,
                    );

                    return (
                      <button
                        key={member.userId}
                        type="button"
                        onClick={() => toggleMemberSelection(member.userId)}
                        className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all duration-200 ${
                          isSelected
                            ? "border-orange-500/50 bg-orange-500/10"
                            : isDarkMode
                              ? "border-gray-700/50 bg-gray-900/30 hover:border-gray-600/50"
                              : "border-gray-200 bg-gray-50 hover:border-gray-300"
                        }`}
                      >
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md flex-shrink-0"
                          style={{
                            backgroundColor: member.color || "#3B82F6",
                          }}
                        >
                          {member.user.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 text-left min-w-0">
                          <div
                            className={`text-sm font-semibold truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            {member.user.name}
                          </div>
                          <div
                            className={`text-xs capitalize ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            {member.role}
                          </div>
                        </div>
                        <div
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all flex-shrink-0 ${
                            isSelected
                              ? "bg-orange-600 border-orange-600"
                              : isDarkMode
                                ? "border-gray-600"
                                : "border-gray-300"
                          }`}
                        >
                          {isSelected && (
                            <CheckCircle size={14} className="text-white" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {formData.selectedMembers.length > 0 && (
                <div
                  className={`mt-3 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  {formData.selectedMembers.length} member
                  {formData.selectedMembers.length !== 1 ? "s" : ""} selected
                </div>
              )}
            </div>
          </form>
        </div>

        {/* Footer Actions */}
        <div
          className={`border-t p-6 ${
            isDarkMode
              ? "border-gray-800/50 bg-gradient-to-br from-[#0C0E12] to-[#0A0C10]"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className={`flex-1 px-5 py-3 border rounded-xl font-medium transition-all duration-200 ${
                isDarkMode
                  ? "bg-gray-800/50 hover:bg-gray-800 border-gray-700/50 hover:border-gray-600/50 text-gray-300 hover:text-white"
                  : "bg-white hover:bg-gray-100 border-gray-200 hover:border-gray-300 text-gray-600 hover:text-gray-900"
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={loading || loadingMembers}
              className="flex-1 px-5 py-3 bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white rounded-xl font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-orange-600/20"
            >
              {loading ? (
                <>
                  <Loader className="animate-spin" size={18} />
                  <span>Blocking Time...</span>
                </>
              ) : (
                <>
                  <Ban size={18} />
                  <span>Block Time</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlockTimeModal;
